import Gate from "components/Gate";
import { useCan } from "modules/settings/accessRoles";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dialog, DialogTitle, Divider as MuiDivider } from "@mui/material";
import { spacing } from "@mui/system";
import styled from "styled-components";
import { LeftSidebar } from "layout-blueprints";

import StandardHeader from "../../components/global/StandardHeader";
import RateReferenceTable from "../../modules/rateReference/components/RateReferenceTable";
import RateReferenceForm from "../../modules/rateReference/components/RateReferenceForm";
import {
  createRateReference,
  get as getRateReferences,
  updateRateReference,
} from "../../modules/rateReference/api/actions";
import { get as getVendors } from "../../modules/vendor/api/actions";
import { setSnackbar } from "../../modules/snackbar/api/snackbar";

const Divider = styled(MuiDivider)(spacing);

const RateReferencePage = () => {
  const canEditRates = useCan()("billingRates");
  const dispatch = useDispatch();
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRateReference, setSelectedRateReference] = useState({});

  const rateReferences = useSelector(
    (state) => state.rateReferences.data.rateReferencesList.items
  );
  const rateReferencesLoading = useSelector((state) => state.rateReferences.isLoading);
  const vendors = useSelector((state) => state.vendors.data.vendorsList.items);

  useEffect(() => {
    document.title = "Ubil+ Rate References";
    dispatch(getRateReferences()).catch(() => {
      dispatch(setSnackbar(true, "error", "Failed to fetch rate reference data."));
    });
    dispatch(getVendors()).catch(() => {
      dispatch(setSnackbar(true, "error", "Failed to fetch vendors."));
    });
  }, [dispatch]);

  const openEditDialog = (item) => {
    if (!canEditRates) return;
    setSelectedRateReference(item);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedRateReference({});
  };

  const submitNewRateReference = async (payload) => {
    try {
      await dispatch(createRateReference(payload));
      dispatch(setSnackbar(true, "success", "Successfully created rate reference."));
      return true;
    } catch (error) {
      dispatch(setSnackbar(true, "error", "Failed to create rate reference."));
      return false;
    }
  };

  const submitRateReferenceEdit = async (payload) => {
    try {
      await dispatch(updateRateReference(selectedRateReference.uuid, payload));
      dispatch(setSnackbar(true, "success", "Successfully updated rate reference."));
      return true;
    } catch (error) {
      dispatch(setSnackbar(true, "error", "Failed to update rate reference."));
      return false;
    }
  };

  return (
    <LeftSidebar>
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Rate Reference</DialogTitle>
        <RateReferenceForm
          vendors={vendors}
          onSubmit={submitNewRateReference}
          onClose={() => setNewDialogOpen(false)}
        />
      </Dialog>

      <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Rate Reference</DialogTitle>
        <RateReferenceForm
          initialValue={selectedRateReference}
          vendors={vendors}
          onSubmit={submitRateReferenceEdit}
          onClose={closeEditDialog}
        />
      </Dialog>

      <StandardHeader title="Rate Reference Management" />
      <Divider className="mb-3 mt-3" />

      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "flex-end" }}>
        <Gate capability="billingRates">
        <Button
          onClick={() => setNewDialogOpen(true)}
          size="small"
          variant="contained"
          color="secondary"
        >
          New Rate Reference
        </Button>
        </Gate>
      </div>

      <RateReferenceTable
        rateReferences={rateReferences}
        vendors={vendors}
        isLoading={rateReferencesLoading}
        onEdit={openEditDialog}
      />
    </LeftSidebar>
  );
};

export default RateReferencePage;
